import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../Component/Common/Button";
import {
  updateQuizQuestion,
  deleteQuizQuestion,
  updateQuizPassMarks,
  updateOrgQuizPassMarks,
  updateOrgQuizQuestion,
  deleteOrgQuizQuestion,
} from "../../store/course/course.action";
import { ReactComponent as TrashIcon } from "../../assets/icons/trashIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/EditBtn.svg";
import AddIcon from "@mui/icons-material/Add";
import { TextField } from "@mui/material";

const QuizQuestionsList = ({
  courseId,
  lessonId,
  onBack,
  onAddQuizQuestion,
}) => {
  const dispatch = useDispatch();
  const { tokenData, profile } = useSelector((state) => state.loggedUser);
  const { selectedCourse } = useSelector((state) => state.course);

  // Get the current lesson from Redux store
  const lesson = selectedCourse.lessons.find(
    (lesson) => lesson._id === lessonId,
  );

  const [passMark, setPassMark] = useState(lesson.quiz.passMark);
  const [quizError, setQuizError] = useState("");

  // If lesson not found, show loading or error
  if (!lesson) {
    return (
      <div className="quiz-questions-list">
        <div className="d-flex align-items-center mb-4">
          <Button className="ms-auto" onClick={onBack}>
            Back
          </Button>
          <h2 className="mb-0">Loading...</h2>
        </div>
        <div className="text-center py-5">
          <p className="text-muted">Loading lesson data...</p>
        </div>
      </div>
    );
  }
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    marks: "",
  });
  const [errors, setErrors] = useState({});

  const handleEdit = (index, question) => {
    setEditingIndex(index);
    setEditForm({
      question: question.questionText,
      options: question.options,
      correctAnswer: question.correctOptionIndex,
      marks: question.marks.toString(),
    });
    setErrors({});
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditForm({
      question: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      marks: "",
    });
    setErrors({});
  };

  const handlePassMarkChange = () => {
    if (passMark) {
      if (tokenData.isOrg) {
        dispatch(
          updateOrgQuizPassMarks(courseId, lessonId, lesson.quiz._id, passMark),
        );
      } else {
        dispatch(
          updateQuizPassMarks(courseId, lessonId, lesson.quiz._id, passMark),
        );
      }
    } else {
      setQuizError("Pass Mark is required!");
    }
  };

  const handleSaveEdit = () => {
    // Validation
    if (
      !editForm.question ||
      !editForm.correctAnswer ||
      !editForm.marks ||
      Object.values(editForm.options).some((opt) => !opt)
    ) {
      const newErrors = {};
      if (!editForm.question) newErrors.question = "Question is required!";
      if (Object.values(editForm.options).some((opt) => !opt))
        newErrors.options = "All options are required!";
      if (!editForm.correctAnswer)
        newErrors.correctAnswer = "Correct answer must be selected!";
      if (!editForm.marks) newErrors.marks = "Marks are required!";
      setErrors(newErrors);
      return;
    }

    const updatedQuizData = {
      questionText: editForm.question,
      options: editForm.options,
      correctOptionIndex: Number(editForm.correctAnswer),
      marks: Number(editForm.marks),
    };

    const updatedQuiz = { ...lesson.quiz };
    updatedQuiz.questions[editingIndex] = updatedQuizData;

    if (tokenData.isOrg) {
      dispatch(updateOrgQuizQuestion(courseId, lesson._id, updatedQuiz));
    } else {
      dispatch(updateQuizQuestion(courseId, lesson._id, lesson.quiz));
    }

    setEditingIndex(null);
    setEditForm({
      question: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      marks: "",
    });
  };

  const handleDelete = (index) => {
    if (window.confirm("Are you sure you want to delete this quiz question?")) {
      let updatedQuiz = { ...lesson.quiz };
      updatedQuiz["questions"] = updatedQuiz["questions"].filter(
        (_, i) => i !== index,
      );

      if (tokenData.isOrg) {
        dispatch(deleteOrgQuizQuestion(courseId, lesson._id, updatedQuiz));
      } else {
        dispatch(deleteQuizQuestion(courseId, lesson._id, updatedQuiz));
      }
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleOptionChange = (index, value) => {
    setEditForm((prev) => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return {
        ...prev,
        options: newOptions,
      };
    });
    if (errors.options) {
      setErrors((prev) => ({
        ...prev,
        options: "",
      }));
    }
  };

  const isCreator =
    tokenData._id === selectedCourse.creatorId ||
    profile?.role === "org_admin" ||
    profile?.role === "org_owner";
  const isCoursePublished = selectedCourse.isPublished;

  return (
    <div className="quiz-questions-list">
      <div className="d-flex align-items-center mb-4">
        <Button className="ms-auto" onClick={onBack}>
          Back
        </Button>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0">{lesson.title} - Quiz Questions</h2>

        {isCreator && !isCoursePublished ? (
          <div className="new-fake-btn">
            <Button
              className={"rounded"}
              startIcon={<AddIcon />}
              onClick={() => onAddQuizQuestion(lessonId, lesson.quiz)}
            >
              Add
            </Button>
          </div>
        ) : null}
      </div>

      {/* {isCreator ? (
        <div className="mb-4">
          <div className="d-flex align-items-start gap-2">
            <p className="mb-0 mt-2">Passing Percentage :</p>
            <TextField
              placeholder="Percentage (%)"
              size="small"
              value={passMark}
              onChange={(e) => {
                setPassMark(e.target.value);
                setQuizError("");
              }}
              onBlur={() => handlePassMarkChange()}
              error={quizError}
              helperText={quizError}
            />
          </div>
        </div>
      ) : null} */}

      {/* Questions List */}
      {!lesson.quiz || lesson.quiz.questions.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">
            No quiz questions available for this lesson.
          </p>
        </div>
      ) : (
        <div className="row">
          {lesson.quiz.questions.map((question, index) => (
            <div key={index} className="col-12 mb-4">
              <div className="card rounded-4 overflow-hidden">
                <div className="card-header lesson-quiz-top d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Question {index + 1}</h5>
                  {isCreator && !isCoursePublished ? (
                    <div className="d-flex gap-2">
                      <Button
                        className="p-0 bg-light icon-btn"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(index, question)}
                      />
                      <Button
                        className="p-0 bg-light icon-btn"
                        startIcon={<TrashIcon />}
                        onClick={() => handleDelete(index)}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="card-body">
                  {editingIndex === index ? (
                    // Edit Form
                    <div className="row gap-3">
                      <div className="col-12">
                        <label className="form-label">Question</label>
                        <textarea
                          className={`form-control ${
                            errors.question ? "is-invalid" : ""
                          }`}
                          value={editForm.question}
                          onChange={(e) =>
                            handleInputChange("question", e.target.value)
                          }
                          rows="3"
                          placeholder="Enter your question..."
                        />
                        {errors.question && (
                          <div className="errorMessage">{errors.question}</div>
                        )}
                      </div>

                      <div className="col-12">
                        <label className="form-label">Options</label>
                        {editForm.options.map((option, index) => (
                          <div
                            key={index}
                            className="d-flex align-items-center gap-2 mb-2 ps-2"
                          >
                            <label className="fw-bold">
                              {indexToCharactor(index)}.
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.options ? "is-invalid" : ""
                              }`}
                              value={option}
                              onChange={(e) =>
                                handleOptionChange(index, e.target.value)
                              }
                              placeholder={`Option ${indexToCharactor(
                                index,
                              )}...`}
                            />
                          </div>
                        ))}
                        {errors.options && (
                          <div className="errorMessage mt-1">
                            {errors.options}
                          </div>
                        )}
                      </div>

                      <div className="col-12 d-flex gap-3">
                        <div className="flex-grow-1">
                          <label className="form-label">Correct Answer</label>
                          <select
                            className={`form-select ${
                              errors.correctAnswer ? "is-invalid" : ""
                            }`}
                            value={editForm.correctAnswer}
                            onChange={(e) =>
                              handleInputChange("correctAnswer", e.target.value)
                            }
                          >
                            <option value="">Select correct answer...</option>
                            {editForm.options.map(
                              (option, index) =>
                                option && (
                                  <option key={index} value={index}>
                                    {indexToCharactor(index)}. {option}
                                  </option>
                                ),
                            )}
                          </select>
                          {errors.correctAnswer && (
                            <div className="errorMessage">
                              {errors.correctAnswer}
                            </div>
                          )}
                        </div>

                        <div style={{ width: "120px" }}>
                          <label className="form-label">Marks</label>
                          <input
                            type="number"
                            className={`form-control ${
                              errors.marks ? "is-invalid" : ""
                            }`}
                            value={editForm.marks}
                            onChange={(e) =>
                              handleInputChange("marks", e.target.value)
                            }
                            placeholder="Marks"
                          />
                          {errors.marks && (
                            <div className="invalid-feedback">
                              {errors.marks}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 d-flex gap-2">
                        <Button
                          className="secondary"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                        <Button className="submit" onClick={handleSaveEdit}>
                          Update
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <>
                      <h6 className="card-title">{question.questionText}</h6>

                      <div className="mb-3">
                        <strong>Options:</strong>
                        <ul className="list-unstyled mt-2">
                          {question.options.map((option, index) => (
                            <li
                              key={index}
                              className={`mb-1 ${
                                question.correctAnswer === index
                                  ? "text-success fw-bold"
                                  : ""
                              }`}
                            >
                              <span className="badge bg-light text-dark me-2">
                                {indexToCharactor(index)}
                              </span>
                              {option}
                              {question.correctOptionIndex === index && (
                                <span className="badge bg-success ms-2">
                                  ✓ Correct
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// index to charactor
const indexToCharactor = (index) => {
  return String.fromCharCode(index + "a".charCodeAt(0));
};

export default QuizQuestionsList;
